import { rd, RemoteData } from '@passionware/monads';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormField } from 'v5/design-system/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from 'v5/design-system/Select';
import { Skeleton } from 'v5/design-system/Skeleton';
import { renderError } from 'v5/patterns/widgets/renderError';
import { useFieldContext } from '../FieldContext';
import { EmptyField } from './EmptyField';
import { FieldControlBase } from './FieldControlBase';
import type { FieldComponent } from './fieldComponent.types';

export type NetsuiteMasterProject = { id: number; name: string };

/** What the NetSuite integration offers: nothing when it is not enabled. */
export type NetsuiteMasterProjectsState =
  | { type: 'disabled' }
  | { type: 'enabled'; projects: NetsuiteMasterProject[] };

export type NetsuiteMasterProjectFieldConfig = {
  /** React hook: called on every render of the field. */
  useMasterProjects(): RemoteData<NetsuiteMasterProjectsState>;
};

/** Ids as strings for the select; `null` ⇄ `''`. */
function toSelectValue(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

function fromSelectValue(value: string): number | null {
  if (value === '') return null;
  const id = Number(value);
  return Number.isInteger(id) ? id : null;
}

/**
 * NETSUITE_MASTER_PROJECT custom field (legacy
 * `SplashlightNetsuiteMasterProjectSelector`): a select over the master
 * projects the integration returns; a notice when the integration is off.
 * Where the projects come from is the feature's business (registry config).
 */
export function createNetsuiteMasterProjectField(
  config: NetsuiteMasterProjectFieldConfig
): FieldComponent<'netsuite-master-project'> {
  return function NetsuiteMasterProjectField({ spec, path, hideLabel }) {
    const { t } = useTranslation('ds');
    const ctx = useFieldContext();
    const readOnly = ctx.mode === 'display';
    const state = config.useMasterProjects();
    const enabled = rd.mapOrElse(
      state,
      current => current.type === 'enabled',
      false
    );
    const skeleton = <Skeleton className="w-full h-9" />;
    const projectId: number | null = useWatch({ name: path }) ?? null;

    if (readOnly && projectId === null) {
      return <EmptyField spec={spec} path={path} hideLabel={hideLabel} />;
    }

    return (
      <FormField
        name={path}
        // A disabled integration cannot be satisfied, so it is not enforced (legacy parity).
        render={({ field: { onChange, value, ref, onBlur, name } }) => (
          <FieldControlBase spec={spec} hideLabel={hideLabel}>
            {rd
              .fullJourney(state)
              .initially(skeleton)
              .wait(skeleton)
              .catch(renderError)
              .map(current => {
                if (current.type === 'disabled') {
                  return (
                    <p className="text-fg-muted italic">
                      NetSuite integration disabled
                    </p>
                  );
                }
                const selected = fromSelectValue(toSelectValue(value));
                const known =
                  selected === null ||
                  current.projects.some(project => project.id === selected);
                // A stored id the integration no longer lists still displays.
                const options: NetsuiteMasterProject[] = known
                  ? current.projects
                  : [
                      { id: selected, name: `#${selected}` },
                      ...current.projects
                    ];
                return (
                  <Select
                    name={name}
                    value={toSelectValue(value)}
                    onValueChange={next => onChange(fromSelectValue(next))}
                    clearable={!spec.required}
                    onClear={() => onChange(null)}
                  >
                    <SelectTrigger
                      variant={ctx.variant}
                      size={ctx.size}
                      readOnly={readOnly}
                      ref={ref}
                      onBlur={onBlur}
                    >
                      <SelectValue placeholder={t('Select.emptyPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {options.map(project => (
                        <SelectItem key={project.id} value={String(project.id)}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              })}
          </FieldControlBase>
        )}
      />
    );
  };
}
